import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../config/api';
import { jwtDecode } from 'jwt-decode';
import { transferGuestData } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Token'ın geçerli olup olmadığını kontrol et
  const isTokenValid = (token) => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      // Token'ın süresinin dolmasına 5 dakika veya daha az kaldıysa yenile
      return decoded.exp * 1000 > Date.now() + 5 * 60 * 1000;
    } catch {
      return false;
    }
  };

  // Token'ı yenile
  const refreshToken = async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) throw new Error('Token bulunamadı');

      // Eğer zaten bir refresh işlemi devam ediyorsa, onu bekle
      if (refreshToken.promise) {
        return await refreshToken.promise;
      }

      // Yeni bir refresh işlemi başlat
      refreshToken.promise = api.post(
        '/auth/refresh-token',
        {},
        {
          headers: {
            'Authorization': `Bearer ${currentToken}`
          }
        }
      );

      const response = await refreshToken.promise;
      const { token: newToken, user } = response.data;
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setToken(newToken);

      // İşlem tamamlandıktan sonra promise'i temizle
      refreshToken.promise = null;
      return newToken;
    } catch (error) {
      console.error('Token yenileme hatası:', error);
      refreshToken.promise = null;
      handleLogout();
      throw error;
    }
  };

  // İlk yükleme kontrolü için bir flag
  const isInitialMount = useRef(true);

  // Token kontrolü ve otomatik yenileme
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        // Sayfa yenilendiğinde token'ı yenile
        if (isInitialMount.current) {
          console.log('Sayfa yenilendi, token yenileniyor...');
          await refreshToken();
          isInitialMount.current = false;
        } else {
          // Diğer durumlarda token süresini kontrol et
          const decoded = jwtDecode(storedToken);
          const timeUntilExpiry = decoded.exp * 1000 - Date.now();
          
          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log('Token süresi azaldı, yenileniyor...');
            await refreshToken();
          } else {
            console.log('Token hala geçerli, yenileme yapılmıyor');
            // Token geçerliyse user state'ini güncelle
            const userData = JSON.parse(localStorage.getItem('user'));
            setUser(userData);
            setToken(storedToken);
          }
        }
      } catch (error) {
        console.error('Token kontrolü sırasında hata:', error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };

    checkAndRefreshToken();

    // Visibility değiştiğinde kontrol et
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInitialMount.current) {
        checkAndRefreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Axios interceptor'ı ekle
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (
          error.response?.status === 401 && 
          !originalRequest._retry &&
          !originalRequest.url?.includes('refresh-token')
        ) {
          originalRequest._retry = true;
          
          try {
            const token = await refreshToken();
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const isAdmin = user?.is_admin || false;

  const handleLogin = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setToken(token);

      // Misafir verilerini aktar (async olarak, hata olursa da devam et)
      try {
        const transferResult = await transferGuestData();
        
        // Transfer sonucu ne olursa olsun event'i tetikle (0 transfer olsa bile)
        // Bu sayede favoriler ve sepet düzgün yüklenmiş olur
        if (transferResult?.data?.transferred) {
          window.dispatchEvent(new CustomEvent('guestDataTransferred', {
            detail: { 
              cartTransferred: transferResult.data.transferred.cart.added || 0,
              favoritesTransferred: transferResult.data.transferred.favorites.added || 0
            }
          }));
        }
      } catch (transferError) {
        // Sessizce hata yönetimi
      }

      return user;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Giriş yapılırken bir hata oluştu');
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setToken(token);

      // Misafir verilerini aktar (async olarak, hata olursa da devam et)
      try {
        const transferResult = await transferGuestData();
        
        // Transfer sonucu ne olursa olsun event'i tetikle (0 transfer olsa bile)
        // Bu sayede favoriler ve sepet düzgün yüklenmiş olur
        if (transferResult?.data?.transferred) {
          window.dispatchEvent(new CustomEvent('guestDataTransferred', {
            detail: { 
              cartTransferred: transferResult.data.transferred.cart.added || 0,
              favoritesTransferred: transferResult.data.transferred.favorites.added || 0
            }
          }));
        }
      } catch (transferError) {
        // Sessizce hata yönetimi
      }

      return user;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Google ile giriş yapılırken bir hata oluştu');
    }
  };

  const handleRegister = async (email, password, firstName, lastName, phone) => {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        phone
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setToken(token);

      // Misafir verilerini aktar (async olarak, hata olursa da devam et)
      try {
        const transferResult = await transferGuestData();
        
        // Transfer sonucu ne olursa olsun event'i tetikle (0 transfer olsa bile)
        // Bu sayede favoriler ve sepet düzgün yüklenmiş olur
        if (transferResult?.data?.transferred) {
          window.dispatchEvent(new CustomEvent('guestDataTransferred', {
            detail: { 
              cartTransferred: transferResult.data.transferred.cart.added || 0,
              favoritesTransferred: transferResult.data.transferred.favorites.added || 0
            }
          }));
        }
      } catch (transferError) {
        // Sessizce hata yönetimi
      }

      return user;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Kayıt olurken bir hata oluştu');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    navigate('/login');
  };

  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!user && !!token && isTokenValid(token);
  };

  const value = {
    user,
    token,
    setUser,
    loading,
    handleLogin,
    handleLogout,
    handleRegister,
    handleGoogleLogin,
    isAuthenticated,
    isAdmin,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;